import { faBan, faCoins, faObjectGroup, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import NoticeCard from 'src/components/ui/NoticeCard';
import { pluralWord } from 'src/utils/stringUtils';

const RESOURCE_ICONS = { printer: faPrint, container: faCoins };

const getResourceLabel = ({ label, type }) =>
  label || (type === 'container' ? 'Container' : 'Printer');

const BlockedResource = ({ type, label, name, materials }) => (
  <div className='notice-card__pair'>
    <div className='notice-card__pair-label'>{getResourceLabel({ label, type })}</div>
    <div className='notice-card__thing'>
      <FontAwesomeIcon icon={RESOURCE_ICONS[type] || faPrint} />
      <span>{name}</span>
    </div>

    <div className='notice-card__pair-label'>Unsupported {pluralWord('material', materials)}</div>
    {materials.map(material => (
      <div key={material} className='notice-card__thing'>
        <FontAwesomeIcon icon={faObjectGroup} />
        <span>{material}</span>
      </div>
    ))}
  </div>
);

/**
 * Why it is blocked, in the destination's own words. Several flows unload into
 * more than one container at a time, so the wording follows the set rather than
 * assuming the first entry speaks for all of them.
 */
const explainBlock = blocked => {
  const [first] = blocked;
  const label = getResourceLabel(first);
  const sameLabel = blocked.every(resource => getResourceLabel(resource) === label);
  const word = (sameLabel ? label : 'destination').toLowerCase();

  const rule =
    blocked.length > 1
      ? `${capitalize(pluralWord(word, blocked))} can only take the material types they are configured for.`
      : `A ${word} can only take the material types it is configured for.`;

  return `${rule} Choose a different destination, or a batch of a supported material type.`;
};

/**
 * The destination cannot take this material at all. A hard block, independent of
 * the blend check - either can fire without the other, so they stay separate
 * cards and this one sits underneath.
 *
 * Takes a list because some flows unload into several containers at once.
 */
const UnsupportedMaterialsAlert = ({ actionLabel, resources, className }) => {
  const [isExpanded, setExpanded] = useState(false);
  const blocked = (resources || []).filter(resource => resource?.materials?.length);

  if (!blocked.length) {
    return null;
  }

  return (
    <NoticeCard
      variant='danger'
      icon={faBan}
      tile='notice-tile--danger'
      title={`Cannot ${actionLabel}`}
      className={className}
      moreLabel='Why this is blocked'
      isOpen={isExpanded}
      sub={blocked.map(resource => (
        <BlockedResource
          key={`${resource.name}-${resource.materials.join()}`}
          type={resource.type}
          label={resource.label}
          name={resource.name}
          materials={resource.materials}
        />
      ))}
      onToggle={() => setExpanded(open => !open)}
    >
      <div className='notice-card__note'>{explainBlock(blocked)}</div>
    </NoticeCard>
  );
};

BlockedResource.propTypes = {
  type: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  materials: PropTypes.arrayOf(PropTypes.string).isRequired,
};

BlockedResource.defaultProps = { type: 'printer', label: null, name: '' };

UnsupportedMaterialsAlert.propTypes = {
  actionLabel: PropTypes.string,
  resources: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['printer', 'container']),
      // Overrides the caption when the destination is neither, e.g. "Cyclone".
      label: PropTypes.string,
      name: PropTypes.string,
      materials: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  className: PropTypes.string,
};

UnsupportedMaterialsAlert.defaultProps = {
  actionLabel: 'continue',
  resources: [],
  className: 'mb-4',
};

export default UnsupportedMaterialsAlert;
